import blueimpLoadImage, {
	LoadImageOptions as blueimpLoadImageOptions,
	MetaData,
} from 'blueimp-load-image';
import faceRecognition from 'controllers/face-recognition';
import merge from 'deepmerge';
import { ERRORS_INVALID_REQUEST_DATA } from 'settings/errors';
import vintagePresets from 'settings/filters';
import store, { AppStateModule } from 'store';
import _ from 'underscore';
import annotateError from 'utils/annotate-error';
import describeImageSource from 'utils/describe-image-source';
import {
	getImagePreviewFailureKind,
	markImagePreviewFailure,
} from 'utils/image-load-failure';
import {
	copyImageHeaderDiagnostics,
	type ImageHeaderDiagnostics,
} from 'utils/inspect-image-header';
import reportError, { markErrorReported } from 'utils/report-error';
/*
 * TODO: add type declaration for vintagejs
 */
// @ts-expect-error: No declaration file
import vintagejs from 'vintagejs';

export type ImageLoadFailureDiagnostics = {
	/** Logical S3 bucket selected before the upload starts. */
	autoExpireBucket?: string;
	/** Whether the correlated auto-expire object is planned or uploaded. */
	autoExpireUploadState?: 'planned' | 'uploaded';
	/** MIME type supplied by the browser's File object. */
	declaredMime?: string;
	/** Privacy-safe bounded header classification read during MIME detection. */
	header?: ImageHeaderDiagnostics;
	/** How Creator selected resolvedMime. */
	mimeSource?: 'browser-declared' | 'extension' | 'magic-bytes';
	/** Accepted MIME type selected by Creator's file detection. */
	resolvedMime?: string;
	/** S3 source category shared with process-photo diagnostics. */
	sourceKind?: 's3';
	/** Bounded call-site identifier used to separate load phases. */
	stage: 'photo-size' | 'preview-render' | 'upload-detail-probe';
	/** MIME detected from the file bytes, when detection produced one. */
	sniffedMime?: string;
	/** Opaque upload correlation id; also forms the S3 object basename. */
	uploadId?: string;
};

export interface LoadImageOptions {
	/** Allow loading the image from the browser cache? */
	cache?: boolean;
	/**
	 * The following enumerated attribute indicates if the fetching of the related image must be done using CORS or not.
	 * CORS-enabled images can be reused in the <canvas> element without being tainted.
	 */
	crossOrigin?: string | null;
	/** Which image effect to apply to the loaded photo? */
	effect?: string | null;
	/** Perform face recognition and return bounded box? */
	faceRecognition?: boolean;
	/** Privacy-safe correlation fields attached only when the load fails. */
	failureDiagnostics?: ImageLoadFailureDiagnostics;
	/**
	 * If input is File, should we force a copy of the loaded object?
	 * In case we need to use the image.src in the resolve callback
	 * we should set this to true (as object url will be revoked)
	 */
	forceCopy?: boolean;
	/** Parse and return meta data in image? */
	meta: boolean;
	/** Use orientation number to rotate the photo to a normalized orientation */
	orientation: number | boolean;
	/** In what image format should the output loaded image be? */
	outputFormat: 'jpeg' | 'png' | 'svg+xml';
	/** How many image loading processes should we run parallel at max? */
	parallel: number;
	/** Set maximum pixel size for width and height of photo */
	scale?: number;
	/** Apply tools to minimize aliasing? */
	smoothing?: boolean;
}

interface ReturnData {
	image: HTMLImageElement;
	data?: ReturnMetaData;
}

interface ReturnMetaData extends MetaData {
	faces?: {
		boundingBox?: {
			x1: number;
			x2: number;
			y1: number;
			y2: number;
		};
	};
}

/**
 * Build the allowlisted metadata recorded for a browser image-load failure.
 *
 * URL and File descriptions are sanitized by {@link describeImageSource}; the
 * optional diagnostics contain only opaque ids, MIME values, and the logical
 * S3 bucket. Filename-derived object keys are excluded even from legacy callers.
 *
 * @param input Image source passed to blueimp.
 * @param blueimpOptions Effective blueimp options for this attempt.
 * @param diagnostics Optional safe call-site correlation fields.
 * @returns Metadata suitable for the Bugsnag `imageData` section.
 */
function buildFailureImageData(
	input: string | File,
	blueimpOptions: blueimpLoadImageOptions,
	diagnostics?: ImageLoadFailureDiagnostics,
): Record<string, unknown> {
	const imageData: Record<string, unknown> = {
		...blueimpOptions,
		src: describeImageSource(input),
	};

	if (!diagnostics) {
		return imageData;
	}

	return {
		...imageData,
		autoExpireBucket: diagnostics.autoExpireBucket,
		autoExpireUploadState: diagnostics.autoExpireUploadState,
		declaredMime: diagnostics.declaredMime,
		header: copyImageHeaderDiagnostics(diagnostics.header),
		mimeSource: diagnostics.mimeSource,
		resolvedMime: diagnostics.resolvedMime,
		sniffedMime: diagnostics.sniffedMime,
		sourceKind: diagnostics.sourceKind,
		stage: diagnostics.stage,
		uploadId: diagnostics.uploadId,
	};
}

/**
 * Normalize a decoded element, validate browser image state, and optionally
 * enrich metadata with face recognition.
 *
 * @param image Decoded image or canvas returned by blueimp.
 * @param options Effective rendering and recognition options.
 * @param data Metadata extracted by blueimp.
 * @param faceBox Previously detected face bounds for recursive completion.
 * @returns A validated image element and enriched metadata.
 */
function imageLoaded(
	image: HTMLImageElement | HTMLCanvasElement,
	options: LoadImageOptions,
	data?: MetaData,
	faceBox?: {
		x1?: number|null;
		x2?: number|null;
		y1?: number|null;
		y2?: number|null;
	},
): Promise<ReturnData> {
	if (image instanceof HTMLCanvasElement) {
		/** Convert blueimp's canvas output back into the promised image shape. */
		const base64 = image.toDataURL(`image/${options.outputFormat}`);

		return new Promise((resolve, reject) => {
			const img = new Image();
			img.onload = () => {
				imageLoaded(
					img,
					options,
					data,
					faceBox,
				).then(
					resolve,
					reject,
				);
			};
			img.onerror = () => {
				reject(new Error('Could not convert canvas to image element'));
			};
			img.src = base64;
		});
	}

	if (!validateImage(image)) {
		throw markImagePreviewFailure(
			new Error('Invalid image'),
			'validation',
		);
	}

	if (
		!faceBox
		&& options.faceRecognition
	) {
		return faceRecognition
			.detectFaceBox(image)
			.catch(() => ({}))
			.then((boundingBox) => imageLoaded(
				image,
				options,
				data,
				boundingBox,
			));
	}

	const returnMetaData: ReturnMetaData = _.extend(
		{
			faces: {
				boundingBox: faceBox,
			},
		},
		data,
	);

	return Promise.resolve({
		image,
		data: returnMetaData,
	});
}

/**
 * Run blueimp and normalize its callback into a Promise with one Bugsnag event
 * for decode failures.
 *
 * The rejected error is marked after the explicit report so an outer
 * best-effort inspection boundary can avoid reporting the same failure again.
 *
 * @param input URL or local file to decode.
 * @param blueimpOptions Effective blueimp options.
 * @param diagnostics Optional privacy-safe failure context.
 * @returns The decoded image and metadata.
 */
function loadImageWithBlueimp(
	input: string|File,
	blueimpOptions: blueimpLoadImageOptions,
	diagnostics?: ImageLoadFailureDiagnostics,
): Promise<{
	image: HTMLImageElement | HTMLCanvasElement;
	data: blueimpLoadImage.MetaData | undefined;
}> {
	return new Promise((resolve, reject) => {
		blueimpLoadImage(
			input,
			(image, data) => {
				if (image instanceof HTMLImageElement || image instanceof HTMLCanvasElement) {
					resolve({
						image,
						data,
					});
				} else {
					const loadError = markImagePreviewFailure(
						new Error('Could not load the image'),
						'decode',
					);

					try {
						const imageData: Record<string, unknown> = {
							...buildFailureImageData(
								input,
								blueimpOptions,
								diagnostics,
							),
							failureKind: 'decode',
						};
						const annotatedLoadError = annotateError(
							loadError,
							'imageData',
							imageData,
						);
						const notificationError = annotateError(
							new Error('Could not load the image with blueimp'),
							'imageData',
							imageData,
						);

						const reported = reportError(
							notificationError,
							'warning',
							(event) => {
								event.addMetadata(
									'imageData',
									imageData,
								);
							},
						);

						/*
						 * Preserve the annotated error for callers while marking it only
						 * when the explicit notification above actually ran.
						 */
						reject(
							reported
								? markErrorReported(annotatedLoadError)
								: annotatedLoadError,
						);
					} catch {
						/* Diagnostic enrichment must never leave this Promise pending. */
						reject(loadError);
					}
				}
			},
			blueimpOptions,
		);
	});
}

/**
 * Reject browser image elements that have no natural size or are incomplete.
 *
 * @param image Decoded browser image to validate and detach handlers from.
 * @returns Whether the element is complete and has non-zero dimensions.
 */
function validateImage(
	image: HTMLImageElement,
): boolean {
	/** Avoid circular handler references in older browsers. */
	image.onload = null;

	if (image.naturalHeight + image.naturalWidth === 0) {
		return false;
	}

	if (typeof image.complete !== 'undefined' && image.complete === false) {
		return false;
	}

	/** A validated image no longer needs its error handler. */
	image.onerror = null;

	return true;
}

/**
 * Decode an image source and optionally transform it for Creator rendering.
 *
 * @param input URL or local File to load.
 * @param opts Optional decoding, transformation, and diagnostic settings.
 * @returns The decoded image element and extracted metadata.
 */
export default function loadImage(
	input: string|File,
	opts?: Partial<LoadImageOptions>,
): Promise<ReturnData> {
	const defaults: LoadImageOptions = {
		cache: true, // allow getting the image from the cache (no cache-busting)
		meta: false,
		orientation: false,
		crossOrigin: window.glPlatform != 'server' && typeof input === 'string' && input.substring(
			0,
			4,
		) == 'http' ? 'Anonymous' : null,
		scale: 0,
		parallel: 5,
		effect: null,
		forceCopy: false,
		outputFormat: 'jpeg',
	};
	const options: LoadImageOptions = opts ? merge(
		defaults,
		opts,
	) : defaults;

	if (!input || (typeof input === 'string' && input.length === 0)) {
		return Promise.reject(new Error(ERRORS_INVALID_REQUEST_DATA));
	}

	if (AppStateModule.imageLoadCount > options.parallel) {
		// Maximum of five parallel image loads
		return new Promise((resolve, reject) => {
			const f = store.watch(
				(state) => state.appstate.imageLoadCount,
				(imageLoadCount) => {
					if (imageLoadCount < options.parallel) {
						f();
						loadImage(
							input,
							options,
						).then(
							resolve,
							reject,
						);
					}
				},
			);
		});
	}

	if (
		typeof input === 'string'
		&& !options.cache
	) {
		// Add cache busting parameter to url
		input += `?${new Date().getTime()}`;
	}

	const blueimpOptions: blueimpLoadImageOptions = {
		canvas: (
			options.smoothing
			|| (
				input instanceof File
				&& options.forceCopy
			)
		),
		meta: options.meta,
		orientation: options.orientation,
	};

	if (options.crossOrigin) {
		blueimpOptions.crossOrigin = options.crossOrigin;
	}
	if (options.scale) {
		blueimpOptions.maxHeight = options.scale;
		blueimpOptions.maxWidth = options.scale;
	}

	AppStateModule.increaseImageLoad();

	return loadImageWithBlueimp(
		input,
		blueimpOptions,
		options.failureDiagnostics,
	)
		.then(({ image, data }) => {
			if (
				!options.effect
				|| store.state.config['features.imageEffects'].length === 0
			) {
				return imageLoaded(
					image,
					options,
					data,
				);
			}

			if (!(options.effect in vintagePresets)) {
				throw new Error('Image effect not known to vintagejs');
			}

			return vintagejs(
				image,
				vintagePresets[options.effect],
			)
				.then((res: any) => res.genImage())
				.then((genImage: HTMLImageElement) => imageLoaded(
					genImage,
					options,
					data,
				))
				.catch(() => {
					/** Normalize vintagejs failures at the rendering boundary. */
					throw new Error('Could not generate vintagejs image');
				});
		})
		.catch((error: unknown) => {
			const failureKind = getImagePreviewFailureKind(error);

			try {
				annotateError(
					error,
					'imageData',
					{
						...buildFailureImageData(
							input,
							blueimpOptions,
							options.failureDiagnostics,
						),
						...(
							failureKind
								? {
									failureKind,
								}
								: {}
						),
					},
				);
			} catch {
				/* Failure context is best-effort; preserve the original error. */
			}

			throw error;
		})
		.finally(() => {
			AppStateModule.decreaseImageLoad();
		});
}
